// Company cash as a live Excel model. The inputs (blue cells) drive every month
// through formulas, with the same rules as companyCashStatement:
//   - each month opens at last month's close;
//   - a gap below the minimum close is drawn from the cheapest source with room
//     (ties: AR, then the lower lender number); cheaper sources fill first;
//   - AR can lend its cap % of next month's customer cash and is repaid the next
//     month with one month of fee;
//   - rates are entered as annual IRR; each month charges the monthly equivalent
//     (1 + IRR)^(1/12) - 1, in the Monthly rate column;
//   - lenders charge that monthly rate on the balance at the start of the month,
//     can be drawn from their start month on, and are repaid from cash above the
//     minimum close, most expensive first (ties: the higher number), only in a
//     month with no draw.
// Each formula also carries the value the screen shows, so the file reads right
// before Excel recalculates.

import { formatMonthLabel, forecastStartMonth } from './cashflowFeeExposureHelpers';
import { companyCashStatement, lenderLabel, normalizeCompanyCashInputs, withLenders } from './companyCash';

const MONEY = '#,##0;(#,##0);-';
const NAVY = 'FF1F4E79';
const WHITE = 'FFFFFFFF';
const INPUT_FONT = 'FF0000FF';
const INPUT_FILL = 'FFFFF2CC';
const TOTAL_FILL = 'FFD6E4F0';
const HELPER_FONT = 'FF7F7F7F';
// Spare lender rows, so a lender can be added in Excel.
const SPARE_LENDERS = 2;

function fill(argb) {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb } };
}

export function columnName(column) {
  let name = '';
  let current = column;
  while (current > 0) {
    const remainder = (current - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    current = Math.floor((current - 1) / 26);
  }
  return name;
}

// companyCashLayout fixes where every input and statement row goes.
export function companyCashLayout(lenderCount) {
  const input = {
    openingCash: 4,
    minCloseBalance: 5,
    monthlyOpex: 6,
    capitalAmount: 7,
    capitalMonth: 8,
    arCapPct: 9,
    arRate: 10,
  };
  const sourcesHeader = 12;
  const arSource = 13;
  const lenderSources = Array.from({ length: lenderCount }, (_, index) => arSource + 1 + index);
  const lastSource = arSource + lenderCount;

  let row = lastSource + 3;
  const next = () => {
    const current = row;
    row += 1;
    return current;
  };
  const block = (count) => Array.from({ length: count }, () => next());

  const statement = { title: next() };
  statement.status = next();
  statement.label = next();
  statement.key = next();
  statement.open = next();
  statement.inflowSection = next();
  statement.capital = next();
  statement.drawn = block(lenderCount);
  statement.arDrawn = next();
  statement.returns = next();
  statement.fee = next();
  statement.totalInflow = next();
  statement.outflowSection = next();
  statement.deployed = next();
  statement.repaid = [];
  statement.interest = [];
  for (let index = 0; index < lenderCount; index += 1) {
    statement.repaid.push(next());
    statement.interest.push(next());
  }
  statement.arRepaid = next();
  statement.arInterest = next();
  statement.opex = next();
  statement.totalOutflow = next();
  statement.net = next();
  statement.close = next();
  statement.balancesSection = next();
  statement.balance = block(lenderCount);
  statement.arBalance = next();
  statement.shortfall = next();
  next();
  statement.helperSection = next();
  statement.beforeBorrowing = next();
  statement.need = next();
  statement.surplus = next();
  statement.arLimit = next();
  // Room rows follow the source order (AR, then lenders), to line up with the draw order column.
  statement.arRoom = next();
  statement.room = block(lenderCount);
  statement.drawTotal = next();

  return { input, sourcesHeader, arSource, lenderSources, lastSource, statement };
}

function setInput(sheet, row, label, value, numFmt) {
  sheet.getCell(row, 1).value = label;
  const cell = sheet.getCell(row, 2);
  cell.value = value;
  cell.font = { color: { argb: INPUT_FONT } };
  cell.fill = fill(INPUT_FILL);
  if (numFmt) cell.numFmt = numFmt;
}

function styleSectionRow(sheet, row, lastColumn) {
  for (let column = 1; column <= lastColumn; column += 1) {
    const cell = sheet.getCell(row, column);
    cell.fill = fill(NAVY);
    cell.font = { bold: true, color: { argb: WHITE } };
  }
}

function styleTotalRow(sheet, row, lastColumn) {
  for (let column = 1; column <= lastColumn; column += 1) {
    const cell = sheet.getCell(row, column);
    cell.fill = fill(TOTAL_FILL);
    cell.font = { bold: true };
  }
}

// writeCompanyCashSheet writes the inputs, the sources table and the month-by-month
// statement. series: { months, returns, fee, deployed } from customerCashSeries.
export function writeCompanyCashSheet(sheet, { series, inputs: rawInputs }) {
  // Annual IRR inputs; inputs saved as monthly rates are shown as the IRR they compound to.
  const inputs = normalizeCompanyCashInputs(withLenders(rawInputs || {}));
  const lenders = [
    ...inputs.lenders,
    ...Array.from({ length: SPARE_LENDERS }, () => ({ name: '', cap: 0, rate: 0, startMonth: '' })),
  ];
  const L = lenders.length;
  const layout = companyCashLayout(L);
  const { input, arSource, lenderSources, lastSource, statement: s } = layout;
  const months = series?.months || [];
  const firstColumn = 2;
  const lastColumn = Math.max(firstColumn + months.length - 1, 7);
  const results = companyCashStatement(
    series || { months: [], returns: [], fee: [], deployed: [] },
    withLenders(rawInputs || {})
  );

  sheet.getCell(1, 1).value = 'Company cash — change the blue cells and the statement recalculates';
  sheet.getCell(1, 1).font = { bold: true, size: 12 };

  sheet.getCell(3, 1).value = 'INPUTS';
  styleSectionRow(sheet, 3, 7);
  setInput(sheet, input.openingCash, 'Opening cash', inputs.openingCash, MONEY);
  setInput(sheet, input.minCloseBalance, 'Minimum close balance', inputs.minCloseBalance, MONEY);
  setInput(sheet, input.monthlyOpex, 'Operating expenses per month', inputs.monthlyOpex, MONEY);
  setInput(sheet, input.capitalAmount, 'Capital fund', inputs.capitalAmount, MONEY);
  setInput(sheet, input.capitalMonth, 'Capital arrives (month, YYYY-MM)', inputs.capitalMonth || '');
  setInput(sheet, input.arCapPct, 'AR facility cap % of next month', inputs.arCapPct, '0.00');
  setInput(sheet, input.arRate, 'AR facility fee - annual IRR %', inputs.arRate, '0.00');
  sheet.getCell(input.capitalMonth, 3).value = 'Type the month as text, e.g. 2027-03';
  sheet.getCell(input.capitalMonth, 3).font = { italic: true, color: { argb: HELPER_FONT } };

  const sourceHeaders = [
    'Source', 'Name', 'Cap', 'Annual IRR %', 'Start month (YYYY-MM, empty = from the start)',
    'Draw order', 'Repay order', 'Number', 'Monthly rate % (calculated)',
  ];
  sourceHeaders.forEach((header, index) => {
    sheet.getCell(layout.sourcesHeader, index + 1).value = header;
  });
  styleSectionRow(sheet, layout.sourcesHeader, 9);
  // Monthly rate that compounds to the annual IRR in column D.
  const monthlyRate = (row) => ({
    formula: `((1+$D$${row}/100)^(1/12)-1)*100`,
  });

  // Order 0 is drawn first. Ties go to the lower number (AR is 0); repayment ties to the higher.
  const rates = `$D$${arSource}:$D$${lastSource}`;
  const numbers = `$H$${arSource}:$H$${lastSource}`;
  const lenderRates = `$D$${arSource + 1}:$D$${lastSource}`;
  const lenderNumbers = `$H$${arSource + 1}:$H$${lastSource}`;
  const drawOrder = (row) => ({
    formula: `COUNTIFS(${rates},"<"&$D$${row})+COUNTIFS(${rates},$D$${row},${numbers},"<"&$H$${row})`,
  });
  sheet.getCell(arSource, 8).value = 0;
  sheet.getCell(arSource, 1).value = 'AR facility';
  sheet.getCell(arSource, 2).value = 'Cap is the % above';
  sheet.getCell(arSource, 4).value = { formula: `$B$${input.arRate}`, result: inputs.arRate };
  sheet.getCell(arSource, 6).value = drawOrder(arSource);
  sheet.getCell(arSource, 9).value = monthlyRate(arSource);
  sheet.getCell(arSource, 9).numFmt = '0.0000';
  lenders.forEach((lender, index) => {
    const row = lenderSources[index];
    sheet.getCell(row, 1).value = `Lender ${index + 1}`;
    [[2, lender.name || ''], [3, lender.cap || 0], [4, lender.rate || 0], [5, lender.startMonth || '']].forEach(([column, value]) => {
      const cell = sheet.getCell(row, column);
      cell.value = value;
      cell.font = { color: { argb: INPUT_FONT } };
      cell.fill = fill(INPUT_FILL);
      if (column === 3) cell.numFmt = MONEY;
    });
    sheet.getCell(row, 8).value = index + 1;
    sheet.getCell(row, 9).value = monthlyRate(row);
    sheet.getCell(row, 9).numFmt = '0.0000';
    sheet.getCell(row, 6).value = drawOrder(row);
    sheet.getCell(row, 7).value = {
      formula: `COUNTIFS(${lenderRates},">"&$D$${row})+COUNTIFS(${lenderRates},$D$${row},${lenderNumbers},">"&$H$${row})`,
    };
  });

  // Statement header and labels.
  sheet.getCell(s.title, 1).value = 'STATEMENT';
  styleSectionRow(sheet, s.title, lastColumn);
  const forecastMonth = forecastStartMonth();
  months.forEach((month, index) => {
    const column = firstColumn + index;
    const actual = month < forecastMonth;
    const status = sheet.getCell(s.status, column);
    status.value = actual ? 'Act.' : 'Proj.';
    status.fill = fill(actual ? 'FF70AD47' : 'FF2E75B6');
    status.font = { bold: true, size: 8, color: { argb: WHITE } };
    const label = sheet.getCell(s.label, column);
    label.value = formatMonthLabel(month);
    label.fill = fill(actual ? 'FF70AD47' : 'FF2E75B6');
    label.font = { bold: true, color: { argb: WHITE } };
    const key = sheet.getCell(s.key, column);
    key.value = month;
    key.font = { size: 8, color: { argb: HELPER_FONT } };
  });
  sheet.getCell(s.key, 1).value = 'Month key';
  sheet.getCell(s.key, 1).font = { size: 8, color: { argb: HELPER_FONT } };

  const labels = [
    [s.open, 'Open Balance'],
    [s.inflowSection, '① CASH INFLOW'],
    [s.capital, 'Capital Fund'],
    [s.arDrawn, 'AR Facility - drawn'],
    [s.returns, 'Returns (customers)'],
    [s.fee, 'Fee (customers)'],
    [s.totalInflow, 'Total Cash Inflow'],
    [s.outflowSection, '② CASH OUTFLOW'],
    [s.deployed, 'Growth Investments - credit deployed'],
    [s.arRepaid, 'AR Facility - repaid'],
    [s.arInterest, 'AR Facility - interest'],
    [s.opex, 'Operating expenses'],
    [s.totalOutflow, 'Total Cash Outflow'],
    [s.net, 'Net Cash Flow'],
    [s.close, 'Close Balance'],
    [s.balancesSection, 'Facility balances'],
    [s.arBalance, 'AR Facility - outstanding'],
    [s.shortfall, 'Below minimum close'],
    [s.helperSection, 'Calculation (helper rows)'],
    [s.beforeBorrowing, 'Cash before borrowing and repaying'],
    [s.need, 'Gap below the minimum close'],
    [s.surplus, 'Cash above the minimum close'],
    [s.arLimit, 'AR facility limit (cap % of next month)'],
    [s.arRoom, 'Room - AR facility'],
    [s.drawTotal, 'Drawn this month'],
  ];
  lenders.forEach((lender, index) => {
    const name = lenderLabel(lender, index);
    labels.push([s.drawn[index], `${name} - drawn`]);
    labels.push([s.repaid[index], `${name} - repaid`]);
    labels.push([s.interest[index], `${name} - interest`]);
    labels.push([s.balance[index], `${name} - outstanding`]);
    labels.push([s.room[index], `Room - ${name}`]);
  });
  labels.forEach(([row, label]) => {
    sheet.getCell(row, 1).value = label;
  });
  [s.inflowSection, s.outflowSection, s.balancesSection, s.helperSection].forEach((row) => styleSectionRow(sheet, row, lastColumn));
  [s.open, s.totalInflow, s.totalOutflow, s.net, s.close].forEach((row) => styleTotalRow(sheet, row, lastColumn));
  [s.beforeBorrowing, s.need, s.surplus, s.arLimit, s.arRoom, s.drawTotal, ...s.room].forEach((row) => {
    sheet.getCell(row, 1).font = { color: { argb: HELPER_FONT } };
  });

  const sum = (rows, column) => (rows.length ? rows.map((row) => `${column}${row}`).join('+') : '0');
  months.forEach((month, index) => {
    const column = firstColumn + index;
    const c = columnName(column);
    const p = index > 0 ? columnName(column - 1) : null;
    const n = index < months.length - 1 ? columnName(column + 1) : null;
    const result = results[index] || {};
    const put = (row, formula, value) => {
      const cell = sheet.getCell(row, column);
      cell.value = formula == null ? value : { formula, result: value };
      cell.numFmt = MONEY;
    };
    const key = `${c}$${s.key}`;

    put(s.open, p ? `${p}${s.close}` : `$B$${input.openingCash}`, result.openBalance);
    put(s.capital, `IF(${key}=$B$${input.capitalMonth},$B$${input.capitalAmount},0)`, result.capital);
    put(s.returns, null, series.returns[index] || 0);
    put(s.fee, null, series.fee[index] || 0);
    put(s.deployed, null, series.deployed[index] || 0);
    put(s.arRepaid, p ? `${p}${s.arDrawn}` : '0', result.arRepaid);
    put(s.arInterest, `${c}${s.arRepaid}*$I$${arSource}/100`, result.arInterest);
    put(s.opex, `$B$${input.monthlyOpex}`, result.opex);
    lenders.forEach((_, i) => {
      const source = lenderSources[i];
      put(s.interest[i], p ? `${p}${s.balance[i]}*$I$${source}/100` : '0', result.lenders?.[i]?.interest || 0);
    });

    const interestSum = sum(s.interest, c);
    put(
      s.beforeBorrowing,
      `${c}${s.open}+${c}${s.capital}+${c}${s.returns}+${c}${s.fee}-${c}${s.deployed}-${c}${s.opex}-${c}${s.arRepaid}-${c}${s.arInterest}-(${interestSum})`,
      undefined
    );
    put(s.need, `MAX(0,$B$${input.minCloseBalance}-${c}${s.beforeBorrowing})`, undefined);
    put(s.surplus, `MAX(0,${c}${s.beforeBorrowing}-$B$${input.minCloseBalance})`, undefined);
    put(s.arLimit, n ? `MAX(0,(${n}${s.returns}+${n}${s.fee})*$B$${input.arCapPct}/100)` : '0', undefined);
    put(s.arRoom, `${c}${s.arLimit}`, undefined);
    lenders.forEach((_, i) => {
      const source = lenderSources[i];
      const previous = p ? `${p}${s.balance[i]}` : '0';
      put(
        s.room[i],
        `IF(OR($E$${source}="",$E$${source}<=${key}),MAX(0,$C$${source}-${previous}),0)`,
        undefined
      );
    });

    // Draw: what is left of the gap after every cheaper source has given all its room.
    const roomRange = `${c}${s.arRoom}:${c}${s.room[L - 1]}`;
    const drawOrderRange = `$F$${arSource}:$F$${lastSource}`;
    const drawFormula = (roomRow, sourceRow) => (
      `MIN(${c}${roomRow},MAX(0,${c}${s.need}-SUMIFS(${roomRange},${drawOrderRange},"<"&$F$${sourceRow})))`
    );
    put(s.arDrawn, drawFormula(s.arRoom, arSource), result.arDrawn);
    lenders.forEach((_, i) => {
      put(s.drawn[i], drawFormula(s.room[i], lenderSources[i]), result.lenders?.[i]?.drawn || 0);
    });
    put(s.drawTotal, `${c}${s.arDrawn}+${sum(s.drawn, c)}`, undefined);

    // Repay: cash above the minimum close, most expensive lender first, only without a draw.
    const repayOrderRange = `$G$${arSource + 1}:$G$${lastSource}`;
    lenders.forEach((_, i) => {
      const source = lenderSources[i];
      if (!p) {
        put(s.repaid[i], '0', 0);
        return;
      }
      const previousBalances = `${p}${s.balance[0]}:${p}${s.balance[L - 1]}`;
      put(
        s.repaid[i],
        `IF(${c}${s.drawTotal}>0,0,MIN(${p}${s.balance[i]},MAX(0,${c}${s.surplus}-SUMIFS(${previousBalances},${repayOrderRange},"<"&$G$${source}))))`,
        result.lenders?.[i]?.repaid || 0
      );
    });
    lenders.forEach((_, i) => {
      const previous = p ? `${p}${s.balance[i]}+` : '';
      put(s.balance[i], `${previous}${c}${s.drawn[i]}-${c}${s.repaid[i]}`, result.lenders?.[i]?.balance || 0);
    });
    put(s.arBalance, `${c}${s.arDrawn}`, result.arBalance);

    put(
      s.totalInflow,
      `${c}${s.capital}+${sum(s.drawn, c)}+${c}${s.arDrawn}+${c}${s.returns}+${c}${s.fee}`,
      result.totalInflow
    );
    put(
      s.totalOutflow,
      `${c}${s.deployed}+${sum(s.repaid, c)}+${interestSum}+${c}${s.arRepaid}+${c}${s.arInterest}+${c}${s.opex}`,
      result.totalOutflow
    );
    put(s.net, `${c}${s.totalInflow}-${c}${s.totalOutflow}`, result.netCashFlow);
    put(s.close, `${c}${s.open}+${c}${s.net}`, result.closeBalance);
    put(s.shortfall, `MAX(0,$B$${input.minCloseBalance}-${c}${s.close})`, result.shortfall);
  });

  sheet.getColumn(1).width = 46;
  sheet.getColumn(2).width = 16;
  for (let column = 3; column <= lastColumn; column += 1) sheet.getColumn(column).width = 14;
  sheet.getColumn(5).width = Math.max(sheet.getColumn(5).width || 14, 16);
  sheet.views = [{ state: 'frozen', xSplit: 1, ySplit: 0 }];
  sheet.properties.tabColor = { argb: NAVY };
  return layout;
}
